// Timing rules for the Live/PV/Battery view of Sodistore Home and Pro (MONITOR-253).
//
// The installation logs one record every 10 s and uploads 15 of them as one S3 chunk,
// named `<unix seconds of the newest record, rounded to 10 s>.json`, so a new chunk
// appears about every 150 s. Its exact name is not predictable (rounding jitter,
// slow uploads, resent failed uploads), so the view sweeps backwards from "now" for
// the newest chunk instead of guessing the next name.

export const CHUNK_PERIOD_S = 150; // 15 records x 10 s
// Upload and S3 latency after the chunk's own timestamp.
export const ARRIVAL_GRACE_S = 10;
// A sweep re-checks this many seconds below the previous sweep, because a chunk named
// just before that sweep may not have been uploaded yet when it ran.
export const UPLOAD_MARGIN_S = 30;
// Same window the first probe always used: older than this counts as not connected.
export const MAX_AGE_S = 400;
export const PROBE_STEP_S = 2;
export const RETRY_DELAY_S = 10;
// Backoff once nothing arrived for MAX_AGE_S, to cap S3 requests on offline installations.
export const OFFLINE_BACKOFF_S = [30, 60, 120, 300];
export const MIN_REPLAY_DELAY_MS = 2000;
export const MAX_REPLAY_DELAY_MS = 10000;

// Offsets in seconds before `nowS`, newest first, covering (lowerExclusiveS, nowS].
export const probeOffsets = (
  nowS: number,
  lowerExclusiveS: number,
  stepS: number = PROBE_STEP_S
): number[] => {
  const offsets: number[] = [];
  for (let offset = 0; nowS - offset > lowerExclusiveS; offset += stepS) {
    offsets.push(offset);
  }
  return offsets;
};

// Everything at or below the returned timestamp is already known or already checked.
export const sweepLowerBound = (
  nowS: number,
  lastChunkS: number | null,
  lastSweepS: number | null
): number => {
  let lower = nowS - MAX_AGE_S;
  if (lastChunkS !== null) lower = Math.max(lower, lastChunkS);
  if (lastSweepS !== null) lower = Math.max(lower, lastSweepS - UPLOAD_MARGIN_S);
  return lower;
};

// Seconds to wait before the next sweep.
export const nextPollDelayS = (
  nowS: number,
  lastChunkS: number | null,
  consecutiveMisses: number
): number => {
  if (lastChunkS !== null) {
    const expectedS = lastChunkS + CHUNK_PERIOD_S + ARRIVAL_GRACE_S;
    if (nowS < expectedS) return expectedS - nowS;
    if (nowS - lastChunkS <= MAX_AGE_S) return RETRY_DELAY_S;
  }
  const index = Math.min(
    Math.max(consecutiveMisses - 1, 0),
    OFFLINE_BACKOFF_S.length - 1
  );
  return OFFLINE_BACKOFF_S[index];
};

// Record keys of `chunk` newer than the one shown last, oldest first.
export const recordsToShow = (
  recordKeys: string[],
  lastShownKey: string | null
): string[] =>
  recordKeys
    .filter((key) => lastShownKey === null || Number(key) > Number(lastShownKey))
    .sort((a, b) => Number(a) - Number(b));

// Spreads the remaining records over the time until the next chunk is due, so the
// values keep moving instead of racing through the chunk and then standing still.
export const replayDelayMs = (
  msUntilNextChunk: number,
  recordsLeft: number
): number => {
  if (recordsLeft <= 0) return MIN_REPLAY_DELAY_MS;
  const spread = msUntilNextChunk / recordsLeft;
  return Math.min(MAX_REPLAY_DELAY_MS, Math.max(MIN_REPLAY_DELAY_MS, spread));
};
